import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEdgeFunctionErrors } from "@/hooks/useEdgeFunctionErrors";
import { useLeadFlowMonitorRun } from "@/hooks/useLeadFlowMonitorRun";
import { describeLeadFlowRun } from "@/lib/lead-flow-monitor-run";
import { formatDistanceToNow } from "date-fns";

export function EdgeFunctionErrorsCard() {
  const { hasRole } = useAuth();
  const { data, isLoading, error, refetch } = useEdgeFunctionErrors();
  const isAdmin = hasRole("admin");
  const leadFlowRun = useLeadFlowMonitorRun({ enabled: isAdmin });
  const [sheetOpen, setSheetOpen] = useState(false);
  // Re-render every minute so a monitor that stopped turns stale on an open Dashboard.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!isAdmin) return;
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [isAdmin]);

  if (!isAdmin) return null;

  if (error) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">System Health</CardTitle>
          <AlertTriangle className="h-4 w-4 text-destructive" />
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Unable to load system health</p>
          <Button variant="ghost" size="sm" className="mt-2 h-7 px-2" onClick={() => refetch()}>
            <RefreshCw className="h-3 w-3 mr-1" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">System Health</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  const { errors, totalCount } = data ?? { errors: [], totalCount: 0 };
  const hasErrors = totalCount > 0;
  const latestError = errors[0];
  // A failed background refetch keeps the last good row; its age still turns the line amber.
  const leadFlow = leadFlowRun.data !== undefined
    ? describeLeadFlowRun(leadFlowRun.data, nowMs)
    : leadFlowRun.error
      ? { text: "Lead-flow monitor: couldn't load the last run", warning: false }
      : null;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">System Health</CardTitle>
          {hasErrors ? (
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          )}
        </CardHeader>
        <CardContent>
          {hasErrors ? (
            <div className="space-y-2">
              <p className="text-sm font-medium">
                {totalCount} error{totalCount !== 1 ? "s" : ""} in the last 7 days
              </p>
              {latestError && (
                <div className="text-xs text-muted-foreground space-y-1">
                  <p className="font-medium">{latestError.function_name}</p>
                  <p className="line-clamp-2">{latestError.message}</p>
                  <p>{formatDistanceToNow(new Date(latestError.created_at), { addSuffix: true })}</p>
                </div>
              )}
              <Button variant="ghost" size="sm" className="h-7 px-2 mt-1" onClick={() => setSheetOpen(true)}>
                View all
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">No recent errors</p>
            </div>
          )}
          {leadFlow && (
            <p
              data-testid="lead-flow-monitor-line"
              data-state={leadFlow.warning ? "warning" : "ok"}
              className={`mt-3 text-xs ${leadFlow.warning ? "text-amber-600" : "text-muted-foreground"}`}
            >
              {leadFlow.text}
            </p>
          )}
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edge Function Errors</SheetTitle>
            <SheetDescription>
              {totalCount > 10
                ? `Showing 10 most recent of ${totalCount} total errors in the last 7 days`
                : `${totalCount} error${totalCount !== 1 ? "s" : ""} in the last 7 days`}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {errors.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No errors in the last 7 days.
              </p>
            ) : (
              errors.map((err) => (
                <div key={err.id} className="rounded-lg border p-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{err.function_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(err.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{err.message}</p>
                  {err.request_path && (
                    <p className="text-xs text-muted-foreground">
                      {err.request_method} {err.request_path}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
